import React, { useState, useEffect } from "react";
import {
  Container,
  Alert,
  Form,
  InputGroup,
  Button,
  Toast,
  ToastContainer,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { BulletList } from "react-content-loader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSync } from "@fortawesome/free-solid-svg-icons";
import { useSearchParams } from 'react-router-dom';
import { getApiBaseUrl } from "../utils/apiBaseUrl";
import "./BotSummaryList.css";

// Default sort by All Time, descending
const DEFAULT_SORT = "returnAll";
const DEFAULT_DIRECTION = "desc";
const DEFAULT_PAGE_SIZE = 50;
const MARKETPLACE_HOME_PAGE_SIZE = 100;
const HOME_PAGE_INITIAL_SIZE = 10;
const MARKETPLACE_LISTINGS_PATH = "/marketplace/listings";

// Timeframes used in both sorting & filtering
const TIMEFRAMES = ["returnAll", "return1y", "return6mo", "return3mo", "return30d", "return7d", "return24h"];

const MARKETPLACE_TIMEFRAME_MAP = {
  returnAll: "allTime",
  return1y: "year",
  return6mo: "month6",
  return3mo: "month3",
  return30d: "month",
  return7d: "week",
  return24h: "day",
};

function buildMarketplaceListingsUrl() {
  const apiBase = getApiBaseUrl();
  if (!apiBase) {
    throw new Error("Marketplace API URL is not configured");
  }

  const base = typeof window !== "undefined" && window.location?.origin
    ? window.location.origin
    : "https://botspot.invalid";
  return new URL(`${apiBase.replace(/\/$/, "")}${MARKETPLACE_LISTINGS_PATH}`, base);
}

async function readMarketplaceListingsPayload(response) {
  const contentType = (response.headers?.get?.("content-type") || "").toLowerCase();

  if (typeof response.text !== "function") {
    const payload = typeof response.json === "function" ? await response.json() : {};
    if (!response.ok) {
      throw new Error(payload?.message || payload?.error || `Marketplace listings request failed (${response.status})`);
    }
    return payload;
  }

  const responseText = await response.text();
  const trimmedText = responseText.trim();
  const looksLikeJson = trimmedText.startsWith("{") || trimmedText.startsWith("[");

  if (trimmedText && !contentType.includes("application/json") && !looksLikeJson) {
    const preview = trimmedText.replace(/\s+/g, " ").slice(0, 80);
    throw new Error(
      `Marketplace listings returned non-JSON response (${response.status || "unknown"} ${contentType || "unknown content-type"}): ${preview}`
    );
  }

  let payload = {};
  if (trimmedText) {
    try {
      payload = JSON.parse(trimmedText);
    } catch {
      throw new Error(`Marketplace listings returned malformed JSON (${response.status || "unknown"})`);
    }
  }

  if (!response.ok) {
    throw new Error(payload?.message || payload?.error || `Marketplace listings request failed (${response.status})`);
  }

  return payload;
}

// For cascading sort if sorting by 1Y. NOTE: disabled implementation after changing to 7D
const CASCADE_ORDER_1Y = ["returnAll", "return1y", "return6mo", "return3mo", "return30d", "return7d", "return24h"];

/**
 * Return the numeric "return" for a given timeframe or null if missing.
 */
export function getReturnValue(bot, timeframe) {
  if (!timeframe) return null;
  
  // Find the performance stat for this timeframe
  const stat = bot.performanceStats?.find((x) => x && x.timeframe === timeframe);
  
  if (!stat || !stat.stats || stat.stats.is_data_complete === false) {
    return null;
  }
  
  return stat.stats.return ?? null;
}

/**
 * If a bot has no valid numeric data in any timeframe, we hide it entirely.
 */
export function hasAnyPerformanceData(bot) {
  for (const tf of TIMEFRAMES) {
    const v = getReturnValue(bot, tf);
    if (v !== null) {
      return true; // Break early if a valid timeframe is found
    }
  }
  return false; // No valid timeframe found
}

/**
 * Format numeric return (to one decimal) or "-" if null.
 */
export function formatReturnValue(val) {
  if (val === null) return "-";
  return val.toFixed(1) + "%";
}

function getMarketplaceDisplayName(listing) {
  return (
    listing?.marketplaceTitle ||
    listing?.title ||
    listing?.strategy?.name ||
    "Untitled strategy"
  );
}

function getMarketplaceCreatorName(listing) {
  return (
    listing?.author ||
    listing?.owner?.nickname ||
    listing?.owner?.name ||
    "BotSpot Library"
  );
}

function normalizeMarketplaceListing(listing) {
  const performanceStats = TIMEFRAMES.map((timeframe) => {
    const marketplaceField = MARKETPLACE_TIMEFRAME_MAP[timeframe];
    const rawValue = listing?.live?.[marketplaceField];
    const numericValue =
      rawValue !== null && rawValue !== undefined && rawValue !== "" && !Number.isNaN(Number(rawValue))
        ? Number(rawValue)
        : null;

    return {
      timeframe,
      stats: {
        return: numericValue,
        is_data_complete: numericValue !== null,
      },
    };
  });

  return {
    id: listing.id,
    name: getMarketplaceDisplayName(listing),
    owner: {
      id: listing.ownerId || listing.owner?.id || null,
      nickname: getMarketplaceCreatorName(listing),
    },
    href: `/marketplace/strategy/${listing.id}`,
    ownerHref: listing.ownerId || listing.owner?.id ? `/profile/${listing.ownerId || listing.owner?.id}` : null,
    isMarketplaceListing: true,
    deploymentsCount: listing.deploymentsCount,
    performanceStats,
  };
}

/**
 * Render a table cell for a bot's return value for a given timeframe.
 * Usage: <ReturnValueCell bot={bot} timeframe={col.timeframe} idx={idx} />
 */
export function ReturnValueCell({ bot, timeframe, idx }) {
  const val = getReturnValue(bot, timeframe);
  const display = formatReturnValue(val);
  const cellClass =
    val === null
      ? "neutral"
      : val > 0
      ? "positive"
      : "negative";
  return (
    <td key={idx}>
      <span className={cellClass}>{display}</span>
    </td>
  );
}

export default function BotSummaryList({ isHomePage = false, homeSearchQuery = "" }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [bots, setBots] = useState([]);
  const [filteredBots, setFilteredBots] = useState([]);
  const [displayedBots, setDisplayedBots] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || DEFAULT_SORT);
  const [sortDirection, setSortDirection] = useState(searchParams.get('direction') || DEFAULT_DIRECTION);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || "");
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1);
  const [showingMore, setShowingMore] = useState(false);

  const [showErrorToast, setShowErrorToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showNoBotsAlert, setShowNoBotsAlert] = useState(false);

  // Use homeSearchQuery for home page, regular searchQuery otherwise
  const effectiveSearchQuery = isHomePage ? homeSearchQuery : searchQuery;

  const isMarketplaceHome = isHomePage;

  const columns = [
    { label: "Strategy Name", key: "name", timeframe: null },
    { label: "All Time", key: "returnAll", timeframe: "returnAll" },
    { label: "1 Y", key: "return1y", timeframe: "return1y" },
    { label: "6 M", key: "return6mo", timeframe: "return6mo" },
    { label: "3 M", key: "return3mo", timeframe: "return3mo" },
    { label: "30 D", key: "return30d", timeframe: "return30d" },
    { label: "7 D", key: "return7d", timeframe: "return7d" },
    { label: "24 H", key: "return24h", timeframe: "return24h" },
    { label: isMarketplaceHome ? "Creator" : "Owner", key: "owner", timeframe: null },
  ];

  /**
   * If we are sorting by 1Y, use fallback timeframes in order:
   *    [1Y, 6M, 3M, 30D, 24H].
   * Return the first non-null value found (this was the old approach).
   * NOTE: We still keep this function for reference, but we'll switch to
   *       a more general multi-tier approach for *all* timeframes.
   *
   * NOTE: 4/22//2025 disabling this since the default sort is now 7D.
   * 
  const getCascadedValueFor1Y = (bot) => {
    for (const tf of CASCADE_ORDER_1Y) {
      const val = getReturnValue(bot, tf);
      if (val !== null) return val;
    }
    return null; // If all are null
  };*/

  /**
   * Maps a numeric value or null into something comparable.
   * If val is null, use Number.POSITIVE_INFINITY so it sorts to the bottom.
   */
  function comparableValue(val) {
    if (val === null) return Number.POSITIVE_INFINITY;
    return val;
  }

  /**
   * Reorder TIMEFRAMES so the currently clicked timeframe is compared first,
   * followed by the others in the default sequence.
   */
  function getTimeframePriority(clickedKey) {
    // If the user clicked "name", there's no reordering to do
    if (clickedKey === "name") {
      return [];
    }
    // The default order is [1Y, 6M, 3M, 30D, 24H]
    const baseOrder = [...TIMEFRAMES];
    const idx = baseOrder.indexOf(clickedKey);
    if (idx === -1) {
      // If somehow not found, just return the entire base order
      return baseOrder;
    }
    // Reorder so that the clicked timeframe is first, then wrap around
    return [...baseOrder.slice(idx), ...baseOrder.slice(0, idx)];
  }

  /**
   * Compare two numeric values (which may be null),
   * always putting null at the bottom for both asc and desc.
   */
  function compareValues(aVal, bVal, direction) {
    // If exactly one is null, that one goes after the other:
    if (aVal === null && bVal !== null) return 1;   // 'a' goes after 'b'
    if (bVal === null && aVal !== null) return -1; // 'b' goes after 'a'

    // If both null, they're tied on this tier
    if (aVal === null && bVal === null) return 0;

    // Otherwise, both are real numbers; do normal numeric compare
    return direction === "asc" 
      ? aVal - bVal 
      : bVal - aVal;
  }

  /**
   * Multi-tier compare logic:
   * - We look at each timeframe in priority order
   * - Compare them (with the above function)
   * - If there's a difference, return it
   * - Otherwise keep going
   */
  function multiTierCompare(a, b, timeframes, direction) {
    for (const tf of timeframes) {
      const aVal = getReturnValue(a, tf);
      const bVal = getReturnValue(b, tf);
      const cmp = compareValues(aVal, bVal, direction);
      if (cmp !== 0) {
        return cmp;
      }
    }
    return 0; // Tied across all timeframes
  }

  async function fetchBots() {
    try {
      setIsLoading(true);
      const url = buildMarketplaceListingsUrl();

      url.searchParams.set("sort", "live_allTime");
      url.searchParams.set("pageSize", String(isMarketplaceHome ? MARKETPLACE_HOME_PAGE_SIZE : DEFAULT_PAGE_SIZE));

      const response = await fetch(url, { headers: { "Accept": "application/json" } });
      const data = await readMarketplaceListingsPayload(response);

      const listings = data?.items || data?.listings || [];
      if (response.ok && Array.isArray(listings)) {
        setBots(listings.map(normalizeMarketplaceListing));
      } else {
        console.error("Invalid response structure:", data);
        setBots([]);
      }
    } catch (error) {
      console.warn(
        isMarketplaceHome ? "Failed to fetch marketplace listings" : "Failed to fetch bots",
        error?.message || error
      );
      setBots([]);
      setShowNoBotsAlert(true);
      setErrorMessage("Failed to fetch strategies. Please try again later.");
      setShowErrorToast(true);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchBots();
  }, []);

  /**
   * Sort the bots according to the user-chosen column/timeframe,
   * using a tiered approach so that rows with "-" for the clicked timeframe
   * go to the bottom, and ties are broken by subsequent timeframes.
   */
  function sortBots(data) {
    return data.slice().sort((a, b) => {
      // 1) If the user clicked the "name" column, do a simple alphabetical
      if (sortBy === "name") {
        const order = a.name.localeCompare(b.name);
        return sortDirection === "asc" ? order : -order;
      }

      // 2) If the user clicked a the owner column, do a simple alphabetical
      if (sortBy === "owner") {
        const order = (a.owner?.nickname || "").localeCompare(b.owner?.nickname || "");
        return sortDirection === "asc" ? order : -order;
      }
      
      // 2) Otherwise, do a tiered comparison:
      //    reorder the timeframe array so "sortBy" is first, then the others.
      const priority = getTimeframePriority(sortBy);
      return multiTierCompare(a, b, priority, sortDirection);
    });
  }

  // Filter, search, sort, then paginate
  const filterAndPaginateData = () => {
    const data = Array.isArray(bots) ? bots : [];

    // 1) Keep only bots with some valid data
    const dataWithValues = isMarketplaceHome
      ? data
      : data.filter((bot) => hasAnyPerformanceData(bot));

    // 2) Apply search (by name)
    let searchedData = dataWithValues;
    if (effectiveSearchQuery.trim() !== "") {
      const query = effectiveSearchQuery.toLowerCase();
      searchedData = searchedData.filter((bot) =>
        (bot.name || "").toLowerCase().includes(query) ||
        (bot.owner?.nickname || "").toLowerCase().includes(query)
      );
    }

    // 3) Sort
    const sortedData = sortBots(searchedData);

    // 4) For home page, just take the sorted data without pagination
    if (isHomePage) {
      setFilteredBots(sortedData);
      setShowNoBotsAlert(sortedData.length === 0);
      return;
    }

    // 5) Paginate for non-home page
    const startIdx = (currentPage - 1) * DEFAULT_PAGE_SIZE;
    const endIdx = startIdx + DEFAULT_PAGE_SIZE;
    const pageData = sortedData.slice(startIdx, endIdx);

    setFilteredBots(pageData);
    setShowNoBotsAlert(searchedData.length === 0);
  };

  // Handle display logic for home page
  useEffect(() => {
    if (isHomePage) {
      if (showingMore) {
        setDisplayedBots(filteredBots);
      } else {
        setDisplayedBots(filteredBots.slice(0, HOME_PAGE_INITIAL_SIZE));
      }
    } else {
      setDisplayedBots(filteredBots);
    }
  }, [filteredBots, showingMore, isHomePage]);

  useEffect(() => {
    filterAndPaginateData();
  }, [bots, effectiveSearchQuery, sortBy, sortDirection, currentPage]);

  const handleShowMore = () => {
    setShowingMore(true);
  };

  const handleShowLess = () => {
    setShowingMore(false);
  };

  // Renders the arrow for the currently-sorted column
  const getSortArrow = (colKey) => {
    if (sortBy !== colKey) return null;
    return (
      <span className="sort-arrow">
        {sortDirection === "asc" ? "▲" : "▼"}
      </span>
    );
  };

  // Update URL parameters when sorting changes
  const handleSortChange = (newSortBy) => {
    const newDirection = sortBy === newSortBy && sortDirection === 'desc' ? 'asc' : 'desc';
    setSortBy(newSortBy);
    setSortDirection(newDirection);
    
    if (!isHomePage) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('sort', newSortBy);
      newParams.set('direction', newDirection);
      setSearchParams(newParams);
    }
  };

  // Update URL when search changes
  const handleSearchChange = (value) => {
    if (isHomePage) {
      // For home page, we don't update searchQuery state or URL params
      // The search is handled by the parent component via homeSearchQuery prop
      return;
    }
    
    setSearchQuery(value);
    setCurrentPage(1);
    
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set('search', value);
    } else {
      newParams.delete('search');
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  // Update URL when page changes
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    
    if (!isHomePage) {
      const newParams = new URLSearchParams(searchParams);
      if (newPage > 1) {
        newParams.set('page', newPage.toString());
      } else {
        newParams.delete('page');
      }
      setSearchParams(newParams);
    }
  };

  return (
    <div className={`bot-summary-list-page ${isHomePage ? 'home-page-mode' : ''}`}>
      {!isHomePage && (
        <Container fluid className="controls-container">
          <div className="controls-row">
            <div className="search-row d-flex align-items-center justify-content-center flex-wrap gap-2">
              <InputGroup className="search-group">
                <Form.Control
                  type="text"
                  placeholder="Search strategies or owners ..."
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="search-input"
                />
              </InputGroup>
              <Button
                variant="secondary"
                className="refresh-button"
                onClick={fetchBots}
              >
                <FontAwesomeIcon icon={faSync} />
              </Button>
            </div>
          </div>
        </Container>
      )}

      {isLoading && (
        <div className="bot-summary-loading-container">
          <BulletList backgroundColor="#333333" foregroundColor="#555555" />
        </div>
      )}

      {/* Table */}
      {!isLoading && !showNoBotsAlert && displayedBots.length > 0 && (
        <div className="table-wrapper horizontal-scroll mt-3">
          <table className="strategy-table">
            <thead>
              <tr>
                {columns.map((col) => (
                  <OverlayTrigger
                    key={col.key}
                    overlay={<Tooltip>Click to sort by {col.label}</Tooltip>}
                  >
                    <th
                      onClick={() => handleSortChange(col.key)}
                      className={sortBy === col.key ? "sorted" : ""}
                    >
                      {col.label}
                      {getSortArrow(col.key)}
                    </th>
                  </OverlayTrigger>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayedBots.map((bot) => (
                <tr key={bot.id}>
                  {columns.map((col, idx) => {
                    if (col.key === "name") {
                      return (
                      <td key={idx}>
                        <a href={bot.href || `/bot/${bot.id}`} className="link">
                        {bot.name || "Untitled"}
                        </a>
                      </td>
                      );
                    }
                    if (col.key === "owner") {
                      return (
                      <td key={idx}>
                        {bot.ownerHref ? (
                          <a href={bot.ownerHref} className="link">
                            {bot.owner?.nickname || "Anonymous"}
                          </a>
                        ) : (
                          <span>{bot.owner?.nickname || "Anonymous"}</span>
                        )}
                      </td>
                      );
                    }
                    return <ReturnValueCell key={col.key} bot={bot} timeframe={col.timeframe} idx={idx} />;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          
          {/* Show More/Less buttons for home page */}
          {isHomePage && filteredBots.length > HOME_PAGE_INITIAL_SIZE && (
            <div className="home-show-more-container">
              {!showingMore ? (
                <div className="home-show-more-section">
                  <div className="home-more-info">
                    <span className="home-showing-count">
                      Showing top {Math.min(HOME_PAGE_INITIAL_SIZE, filteredBots.length)} of {filteredBots.length} {isMarketplaceHome ? "library strategies" : "trading agents"}
                    </span>
                  </div>
                  <Button
                    className="home-show-more-button"
                    onClick={handleShowMore}
                  >
                    Load More Library Strategies ({filteredBots.length - HOME_PAGE_INITIAL_SIZE} remaining)
                  </Button>
                </div>
              ) : (
                <div className="home-show-less-section">
                  <div className="home-more-info">
                    <span className="home-showing-count">
                      Showing all {filteredBots.length} library strategies
                    </span>
                  </div>
                  <Button
                    className="home-show-less-button"
                    variant="outline-light"
                    onClick={handleShowLess}
                  >
                    Show Top {HOME_PAGE_INITIAL_SIZE} Only
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* No bots found message */}
      {!isLoading && displayedBots.length === 0 && (
        <Alert variant="info" className="mt-3 text-center">
          {isHomePage 
            ? "No library strategies available right now. Check back soon for new strategies."
            : "No strategies found. Please try adjusting your search."
          }
        </Alert>
      )}

      {/* Toast for errors */}
      <ToastContainer position="top-center" className="bot-summary-toast-container">
        <Toast
          show={showErrorToast}
          onClose={() => setShowErrorToast(false)}
          delay={5000}
          autohide
        >
          <Toast.Header>
            <strong className="me-auto">Error</strong>
            <small>Just now</small>
          </Toast.Header>
          <Toast.Body>{errorMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
}
